import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { grey } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'

import { ReactComponent as ToterIcon } from 'assets/inventory/container-type/Toter.svg'
import { TYPE_TO_ICON_MAP } from 'components/settings/pages/inventory/container-types/settings'
import { CONTAINER_STATUS, CONTAINER_STATUS_META } from 'containers/inventory/settings'
import { UNASSIGNED } from 'theme/colors'
import { getFormattedColorAndContrastColor } from 'theme/utils'
import { getMeasureUnit } from 'utils/get-measure-unit'

import type { Theme } from '@mui/material/styles'

type ContainerMarkerPillContainer = {
  name?: string | null
  containerName?: string | null
  status?: string | null
  measure?: { volume?: number | null; unit?: string | null } | null
  containerType?: { type?: string | null } | null
  color?: { color?: string | null } | null
}

type ContainerMarkerPillProps = {
  container: ContainerMarkerPillContainer
  showContainerName?: boolean
}

const getPillColors = (container: ContainerMarkerPillContainer, theme: Theme) => {
  const color = container.color?.color
  const { contrastColor } = getFormattedColorAndContrastColor(color, theme)
  const containerStatus = container.status
  const containerStatusColor = (containerStatus && CONTAINER_STATUS_META[containerStatus]?.color) || UNASSIGNED.main

  if (containerStatus === CONTAINER_STATUS.DECOMMISSIONED) {
    return {
      chipTextColor: theme.palette.common.white,
      chipBgColor: grey[700],
      chipBorderColor: containerStatusColor,
      iconBgColor: '',
      iconColor: theme.palette.common.white,
    }
  }

  return {
    chipTextColor: theme.palette.common.black,
    chipBgColor: theme.palette.common.white,
    chipBorderColor: containerStatusColor,
    iconBgColor: color,
    iconColor: color ? contrastColor : theme.palette.action.active,
  }
}

/**
 * The container pill visual (measure + type icon, status border) for static-map thumbnails.
 * Deliberately mirrors ContainerMarker's inline chip WITHOUT touching that shared component —
 * unify the two when ContainerMarker gets its own TypeScript migration.
 */
const ContainerMarkerPill: React.FC<ContainerMarkerPillProps> = ({ container, showContainerName = false }) => {
  const theme = useTheme()
  const { chipTextColor, chipBgColor, chipBorderColor, iconBgColor, iconColor } = getPillColors(container, theme)

  const type = container.containerType?.type
  const { measure } = container
  const measureLabel = measure ? `${measure.volume} ${getMeasureUnit(measure.unit)}` : ''
  const TypeIcon = (type && TYPE_TO_ICON_MAP[type]) || ToterIcon

  // Maintain backward compatibility with the old container flow
  const rawName = container.containerName || container.name || ''
  const nameMatch = rawName.match(/#(\d+)/)
  const containerName = nameMatch ? nameMatch[1] : rawName
  const chipLabel = showContainerName ? `${measureLabel} #${containerName}` : measureLabel || containerName

  return (
    <Box position="relative">
      <Chip
        label={chipLabel.trim()}
        sx={{
          border: '2px solid',
          borderColor: chipBorderColor,
          borderRadius: 4,
          color: chipTextColor,
          backgroundColor: chipBgColor,
          ...theme.typography.h6,
          fontWeight: 700,
        }}
        {...(TypeIcon && {
          deleteIcon: (
            <Box alignItems="center" display="flex" justifyContent="center" {...(iconBgColor && { bgcolor: iconBgColor })}>
              <SvgIcon fontSize="small" sx={{ ...(iconColor && { color: iconColor }) }}>
                <TypeIcon />
              </SvgIcon>
            </Box>
          ),
          onDelete: () => undefined,
        })}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-8px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderTop: `7px solid ${chipBorderColor}`,
          zIndex: -1,
        }}
      />
    </Box>
  )
}

export default ContainerMarkerPill
